import { createBrowserRouter, RouterProvider } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import RTL from "components/rtl";
import { routes } from "./routes";
import { createCustomTheme } from "./theme";
import "./i18n";
import { AuthProvider } from "contexts/auth0Context";
import TelegramProvider from "contexts/TelegramContext";
import toast, { Toaster } from "react-hot-toast";
import { THEME, TonConnectUIProvider } from "@tonconnect/ui-react";
import useSettings from "./hooks/useSettings";
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import ReactGA from "react-ga4";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Iconify from "./components/Iconify";
import { Backdrop, Box, ButtonBase, Stack, Typography } from "@mui/material";
import FlexBetween from "./components/flexbox/FlexBetween";
import { PrimaryButton } from "./components/Button";
import { useEffect, useState } from "react";
import { useTelegram } from "contexts/TelegramContext";
import axios from 'axios';
import { randomNumber } from "./utils/until";
import { TaskProvider } from "./contexts/task";
import FlexRowAlign from "./components/flexbox/FlexRowAlign";

// if (process.env.REACT_APP_IS_LOCAL !== "true") {
ReactGA.initialize("G-48MMQJMVYW" || "DUMMY");
// }

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export default function App() {
  const { settings } = useSettings();
  const [isVerified, setIsVerified] = useState(process.env.REACT_APP_IS_DEV == 'true' ? true : false)
  const theme = createCustomTheme(settings);
  const router = createBrowserRouter(routes());
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <QueryClientProvider
        client={queryClient}
      >
        <ThemeProvider theme={theme}>
          <TonConnectUIProvider
            manifestUrl='https://codium.network/tonconnect-manifest.json'
            uiPreferences={{ theme: THEME.DARK }}
            walletsListConfiguration={{
              includeWallets: [
                {
                  appName: "safepalwallet",
                  name: "SafePal",
                  imageUrl:
                    "https://s.pvcliping.com/web/public_image/SafePal_x288.png",
                  tondns: "",
                  aboutUrl: "https://www.safepal.com",
                  universalLink: "https://link.safepal.io/ton-connect",
                  deepLink: "safepal-tc://",
                  jsBridgeKey: "safepalwallet",
                  bridgeUrl: "https://ton-bridge.safepal.com/tonbridge/v1/bridge",
                  platforms: ["ios", "android", "chrome", "firefox"],
                },
                {
                  appName: "bitgetTonWallet",
                  name: "Bitget Wallet",
                  imageUrl:
                    "https://raw.githubusercontent.com/bitkeepwallet/download/main/logo/png/bitget%20wallet_logo_iOS.png",
                  aboutUrl: "https://web3.bitget.com",
                  deepLink: "bitkeep://",
                  jsBridgeKey: "bitgetTonWallet",
                  bridgeUrl: "https://bridge.tonapi.io/bridge",
                  platforms: ["ios", "android", "chrome"],
                  universalLink: "https://bkcode.vip/ton-connect",
                },
                {
                  appName: "tonwallet",
                  name: "TON Wallet",
                  imageUrl: "https://wallet.ton.org/assets/ui/qr-logo.png",
                  aboutUrl:
                    "https://chrome.google.com/webstore/detail/ton-wallet/nphplpgoakhhjchkkhmiggakijnkhfnd",
                  universalLink: "https://wallet.ton.org/ton-connect",
                  jsBridgeKey: "tonwallet",
                  bridgeUrl: "https://bridge.tonapi.io/bridge",
                  platforms: ["chrome", "android"],
                },
                {
                  appName: "nicegramWallet",
                  name: "Nicegram Wallet",
                  imageUrl: "https://static.nicegram.app/icon.png",
                  aboutUrl: "https://nicegram.app",
                  universalLink: "https://nicegram.app/tc",
                  deepLink: "nicegram-tc://",
                  jsBridgeKey: "nicegramWallet",
                  bridgeUrl: "https://bridge.tonapi.io/bridge",
                  platforms: ["ios", "android"],
                },
              ],
            }}
            actionsConfiguration={{
              twaReturnUrl: `https://t.me/codium_dev_bot/app`,
            }}>
            <TelegramProvider>
              <AuthProvider>
                <TaskProvider>
                  <RTL>
                    <CssBaseline />
                    {
                      !isVerified ? <ChanelVerify onVerified={() => setIsVerified(true)} />
                        :
                        <RouterProvider router={router} />
                    }
                    <Toaster
                      containerClassName="toast-container"
                      toastOptions={{
                        className: '',
                        duration: 5000,
                        style: {
                          background: `none`,
                          color: '#fff',
                          borderRadius: '0px',
                          boxShadow: 'none',

                        },
                        icon: <Iconify icon={'bx:error-alt'} size={20} />
                      }}
                    />
                  </RTL>
                </TaskProvider>
              </AuthProvider>
            </TelegramProvider>
          </TonConnectUIProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </LocalizationProvider>

  );
}


const ChanelVerify = ({ onVerified }) => {
  const { user } = useTelegram();
  const [isDone, setIsDone] = useState(false)
  const [processing, setProcessing] = useState(false)
  const { id } = user ? user : {}
  const app = window.Telegram?.WebApp;
  const openTelegramLink = app?.openTelegramLink;
  const API = process.env.REACT_APP_IS_DEV == 'true' ? 'http://localhost:2000/api' : `${process.env.REACT_APP_URL}/api`;

  const handleCheck = async (id) => {
    if (!id) return
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "Content-Security-Policy-Report-Only":
        "script-src https://accounts.google.com/gsi/client; frame-src https://accounts.google.com/gsi/; connect-src https://accounts.google.com/gsi/",
    };
    setProcessing(true)
    const response = await axios({
      url: `${API}/telegraf/is-join-chanel?userId=${id}`,
      method: 'GET',
      headers: headers
    });
    if (response?.data == true && onVerified) {
      onVerified()
    } else {
      if (isDone === true) {
        toast.error("You have not joined the channel.")
      }
      setIsDone(true)
    }
    setProcessing(false)
  }

  const handleOpenLink = (link) => {
    if (!link) return
    openTelegramLink(link, { try_instant_view: true });
  }

  useEffect(() => {
    if (process.env.REACT_APP_IS_DEV == 'true') {
      onVerified()
    } else {
      handleCheck(user?.id)
    }
  }, [user])

  useEffect(() => {
    const canvas = document.getElementById('canv');
    const ctx = canvas.getContext('2d');
    const w = canvas.width = document.body.offsetWidth;
    const h = canvas.height = document.body.offsetHeight;

    const cols = Math.floor(w / 20) + 1;
    const ypos = Array(cols).fill(0);

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    function matrix() {
      ctx.fillStyle = '#0001';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = '#0f0';
      ctx.font = '12pt Source Code Pro';
      ypos.forEach((y, ind) => {
        const text = randomNumber(0, 1)
        const x = ind * 20;
        ctx.fillText(text, x, y);
        if (y > 100 + Math.random() * 10000) ypos[ind] = 0;
        else ypos[ind] = y + 20;
      });
    }

    setInterval(matrix, 50);

  }, [])


  return <Backdrop open={true} sx={{ zIndex: 2001 }}>
    {
      isDone ? <Stack alignItems={'center'} spacing={5} justifyContent={'center'} sx={{ position: 'absolute', zIndex: 100, width: '100%' }}>
        <Stack sx={{ position: 'relative', alignItems: 'center' }}>
          <Typography textAlign={'center'} width={'100%'} variant="h4" sx={{ color: '#89EC5D' }}>[ Please join channel ]</Typography>
        </Stack>
        <FlexBetween gap={2} p={2} sx={{ width: "calc(100% - 20px)" }}>
          <PrimaryButton disabled={processing} onClick={() => handleOpenLink('https://t.me/codium_network')} sx={{ height: '40px', width: '100%', background: "#40C5D2", fontSize: '1.2rem', color: "#fff" }}>Open Channel</PrimaryButton>
          <PrimaryButton disabled={processing} onClick={() => handleCheck(id)} sx={{ height: '40px', width: '100%', background: "#9AD864", fontSize: '1.2rem', color: "#fff" }}>Verify</PrimaryButton>
        </FlexBetween>
      </Stack> : <>
        <FlexRowAlign gap={4} sx={{ position: 'absolute', left: '50%', top: '100px', translate: '-50% -50%' }}>
          <ButtonBase onClick={() => window.open('https://x.com/Codium_network', '_blank')}>
            <Stack alignItems={'center'} sx={{ background: "radial-gradient(rgba(0,0,0,1), rgba(0,0,0,0))" }}>
              <Iconify icon={'hugeicons:new-twitter'} size={25} sx={{ filter: "drop-shadow(0px 0px 3px #beffa0)", color: '#89EC5D' }} />
              <Typography variant="h5" sx={{ color: '#89EC5D', fontWeight: 700, whiteSpace: 'nowrap' }} >[ Twitter ]</Typography>
            </Stack>
          </ButtonBase>
          <ButtonBase onClick={() => handleOpenLink('https://t.me/codium_network')} >
            <Stack alignItems={'center'} sx={{ background: "radial-gradient(rgba(0,0,0,1), rgba(0,0,0,0))" }}>
              <Iconify icon={'iconoir:telegram'} size={27} sx={{ filter: "drop-shadow(0px 0px 3px #beffa0)", color: '#89EC5D' }} />
              <Typography variant="h5" sx={{ color: '#89EC5D', fontWeight: 700, whiteSpace: 'nowrap' }} >[ Chanel ]</Typography>
            </Stack>
          </ButtonBase>
        </FlexRowAlign>
        <Stack justifyContent={'center'} alignItems={'center'} sx={{ position: 'absolute', left: '50%', top: '50%', translate: '-50% -50%' }}>
          <Typography sx={{ color: "#89EC5D", textShadow: '0px 0px 10px #000' }} variant="h1">[codium]</Typography>
          <Typography sx={{ color: "#89EC5D", textShadow: '0px 0px 10px #000' }} variant="h6">v1.0.1</Typography>
        </Stack>
        <ButtonBase onClick={() => onVerified()} sx={{ position: 'absolute', left: '50%', bottom: '100px', translate: '-50% 0px', border: '1px solid #89EC5D', fontSize: '1.8rem', fontWeight: 500, color: "#89EC5D", height: '45px', width: '200px', background: 'rgba(0,0,0,0.7)' }}>_START_</ButtonBase>
      </>
    }
    <canvas width="500" height="200" id="canv" style={{ width: '100%', height: '100%' }} />
  </Backdrop>
}

